import React, { useState } from "react";
import api from "../services/api";
import { useNotification } from "../contexts/NotificationContext";

interface Safe {
  id: number;
  isOpen: boolean;
}

export const SafeCrackingPublicPage: React.FC = () => {
  const notification = useNotification();

  // Инициализируем 9 сейфов (3x3)
  const [safes, setSafes] = useState<Safe[]>([
    { id: 1, isOpen: true },
    { id: 2, isOpen: false },
    { id: 3, isOpen: true },
    { id: 4, isOpen: false },
    { id: 5, isOpen: true },
    { id: 6, isOpen: false },
    { id: 7, isOpen: false },
    { id: 8, isOpen: false },
    { id: 9, isOpen: true },
  ]);

  // Состояния для модального окна ввода кода доступа
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState("");

  // Состояния для игры
  const [isCracking, setIsCracking] = useState(false);
  const [validatedAccessCode, setValidatedAccessCode] = useState("");
  const [inputDigits, setInputDigits] = useState<string[]>(["", "", ""]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [failedResults, setFailedResults] = useState<
    ("correct" | "present" | "absent")[]
  >([]);
  const [prizeName, setPrizeName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleSafe = (id: number) => {
    setSafes(
      safes.map((safe) =>
        safe.id === id ? { ...safe, isOpen: !safe.isOpen } : safe,
      ),
    );
  };

  // Открываем модальное окно для ввода кода доступа
  const handleOpenAccessModal = () => {
    setShowAccessCodeModal(true);
    setAccessCode("");
    setAccessCodeError("");
  };

  // Закрываем модальное окно кода доступа
  const handleCloseAccessModal = () => {
    setShowAccessCodeModal(false);
    setAccessCode("");
    setAccessCodeError("");
  };

  // Проверяем код доступа
  const handleCheckAccessCode = async () => {
    if (!accessCode.trim()) {
      setAccessCodeError("Введите код доступа");
      return;
    }

    setAccessCodeError("");
    setIsCheckingAccess(true);
    try {
      const response = await api.post("/safe-codes/check-access", {
        accessCode: accessCode.trim(),
      });

      const { valid, alreadyUsed, prizeAlreadyOpened, message } = response.data;

      if (valid) {
        // Код валидный, начинаем игру
        notification.success(
          "Код доступа принят",
          "У вас есть одна попытка открыть сейф!",
        );
        setValidatedAccessCode(accessCode.trim());
        setShowAccessCodeModal(false);
        setIsCracking(true);
        setInputDigits(["", "", ""]);
        setIsSuccess(false);
        setIsFailed(false);
        setFailedResults([]);
        setPrizeName("");
        // Автофокус на первое поле
        setTimeout(() => {
          document.getElementById("digit-0")?.focus();
        }, 100);
      } else {
        // Код невалидный - показываем ошибку под полем
        if (alreadyUsed) {
          setAccessCodeError("Этот код доступа уже был использован");
        } else if (prizeAlreadyOpened) {
          setAccessCodeError(
            "Приз из этого сейфа уже был получен другим игроком",
          );
        } else {
          setAccessCodeError(
            message || "Неверный код доступа. Проверьте правильность кода",
          );
        }
      }
    } catch (error: any) {
      console.error("Ошибка при проверке кода доступа:", error);
      setAccessCodeError(
        error.response?.data?.message ||
          "Не удалось проверить код доступа. Попробуйте позже",
      );
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (value === "" || /^[0-9]$/.test(value)) {
      const newDigits = [...inputDigits];
      newDigits[index] = value;
      setInputDigits(newDigits);

      // Автоматический переход к следующему полю
      if (value !== "" && index < 2) {
        const nextInput = document.getElementById(`digit-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  // Попытка взлома сейфа (одна попытка на код доступа)
  const checkCode = async () => {
    if (inputDigits.some((d) => d === "")) {
      return;
    }

    setIsSubmitting(true);
    try {
      const attemptCode = inputDigits.join("");
      const response = await api.post("/safe-codes/attempt", {
        accessCode: validatedAccessCode,
        attemptCode,
      });

      const { success, results, prizeName: prize, error } = response.data;

      if (error) {
        notification.error("Ошибка", error);
        setIsCracking(false);
        return;
      }

      // Проверка на успех
      if (success) {
        setIsSuccess(true);
        setPrizeName(prize || "Приз");
        notification.success(
          "🎉 Сейф взломан!",
          `Вы получили приз: ${prize}!`,
          10000,
        );
      } else {
        // Неудача - показываем результат (окно не закрывается автоматически)
        setIsFailed(true);
        setFailedResults(results);
        notification.error(
          "Неправильный код",
          "Код доступа сгорел. Попробуйте получить новый код.",
          8000,
        );
      }
    } catch (error: any) {
      console.error("Ошибка при попытке взлома:", error);
      notification.error(
        "Ошибка",
        error.response?.data?.message || "Не удалось проверить код",
      );
      setIsCracking(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getResultColor = (result: "correct" | "present" | "absent") => {
    switch (result) {
      case "correct":
        return "bg-green-500";
      case "present":
        return "bg-yellow-500";
      case "absent":
        return "bg-red-500";
    }
  };

  return (
    <div
      className="w-full min-h-screen flex items-center justify-center p-8"
      style={{
        backgroundImage: "url(/models/safe-crack/safe-crack-back.png)",
        backgroundRepeat: "repeat",
        backgroundSize: "120px auto",
      }}
    >
      <div className="flex flex-col items-center gap-8">
        {/* Кнопка взлома сейфа */}
        <button
          onClick={handleOpenAccessModal}
          className="w-full max-w-md text-white font-bold text-2xl md:text-3xl transform hover:scale-105 transition-all duration-200 active:scale-95 relative"
          style={{
            backgroundImage: "url(/models/safe-crack/button.png)",
            backgroundSize: "100% 100%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
            filter:
              "drop-shadow(0 0 15px rgba(255, 215, 0, 0.7)) drop-shadow(0 0 30px rgba(255, 215, 0, 0.5)) drop-shadow(0 0 45px rgba(255, 215, 0, 0.3))",
            height: "120px",
            padding: "20px 40px",
          }}
        >
          🔓 Взломать сейф
        </button>

        {/* Сетка сейфов */}
        <div className="grid grid-cols-3 gap-6 md:gap-8 lg:gap-12">
          {safes.map((safe) => (
            <div
              key={safe.id}
              onClick={() => toggleSafe(safe.id)}
              className="cursor-pointer transition-transform hover:scale-105"
              style={{
                width: "120px",
                height: "120px",
              }}
            >
              <img
                src={
                  safe.isOpen
                    ? "/models/safe-crack/opened-safe.png"
                    : "/models/safe-crack/closed-safe.png"
                }
                alt={safe.isOpen ? "Открытый сейф" : "Закрытый сейф"}
                className={`w-full h-full object-contain ${
                  safe.isOpen ? "animate-pulse" : ""
                }`}
                style={{
                  filter: safe.isOpen
                    ? "drop-shadow(0 0 20px rgba(255, 215, 0, 0.8)) drop-shadow(0 0 40px rgba(255, 215, 0, 0.6)) drop-shadow(0 0 60px rgba(255, 215, 0, 0.4))"
                    : "none",
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Модальное окно для ввода кода доступа */}
      {showAccessCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 max-w-md w-full shadow-2xl border-4 border-yellow-600">
            <h2 className="text-3xl font-bold text-yellow-400 mb-6 text-center">
              🔑 Введите код доступа
            </h2>

            <p className="text-white text-center mb-6">
              Введите код доступа, который вы получили при покупке
            </p>

            <input
              type="text"
              value={accessCode}
              onChange={(e) => {
                setAccessCode(e.target.value);
                if (accessCodeError) setAccessCodeError("");
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isCheckingAccess) {
                  handleCheckAccessCode();
                }
              }}
              placeholder="Например: shope123"
              className={`w-full px-4 py-3 text-center text-lg font-mono bg-gray-700 text-white border-4 rounded-lg focus:outline-none focus:ring-4 transition-all ${
                accessCodeError
                  ? "border-red-600 focus:ring-red-400"
                  : "border-yellow-600 focus:ring-yellow-400"
              }`}
              autoFocus
            />

            {accessCodeError && (
              <div className="mt-3 p-3 bg-red-900 bg-opacity-50 rounded-lg border border-red-600">
                <p className="text-red-200 text-sm text-center">
                  {accessCodeError}
                </p>
              </div>
            )}

            <div className={`flex gap-3 ${accessCodeError ? "mt-4" : "mt-6"}`}>
              <button
                onClick={handleCheckAccessCode}
                disabled={isCheckingAccess || !accessCode.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white font-bold text-lg rounded-lg hover:from-yellow-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isCheckingAccess ? "Проверка..." : "Продолжить"}
              </button>
              <button
                onClick={handleCloseAccessModal}
                disabled={isCheckingAccess}
                className="flex-1 py-3 bg-gray-600 text-white font-bold text-lg rounded-lg hover:bg-gray-500 transition-all disabled:opacity-50"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно взлома */}
      {isCracking && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 max-w-md w-full shadow-2xl border-4 border-yellow-600">
            <h2 className="text-3xl font-bold text-yellow-400 mb-6 text-center">
              {isSuccess
                ? `🎉 Сейф взломан!`
                : isFailed
                  ? "❌ Неудача"
                  : "🔐 Взлом сейфа"}
            </h2>

            {isSuccess && (
              <div className="text-center mb-6">
                <p className="text-2xl font-bold text-green-400 mb-4">
                  Поздравляем!
                </p>
                <p className="text-xl text-white mb-6">
                  Вы получили приз:{" "}
                  <span className="font-bold text-yellow-400">{prizeName}</span>
                </p>

                <div className="bg-yellow-900 bg-opacity-50 rounded-lg border-2 border-yellow-600 p-4 mb-4">
                  <p className="text-yellow-200 text-lg font-bold mb-3">
                    📋 Для получения приза:
                  </p>
                  <div className="text-left space-y-2 text-white">
                    <p className="text-sm">1. Обратитесь к менеджеру</p>
                    <p className="text-sm">2. Сообщите ваш код доступа:</p>
                  </div>
                </div>

                <div className="bg-gray-700 rounded-lg p-4 mb-4">
                  <p className="text-gray-400 text-xs mb-2">
                    Ваш код от сейфа:
                  </p>
                  <p className="text-2xl font-mono font-bold text-yellow-400 tracking-wider">
                    {inputDigits.map((c) => c).join("")}
                  </p>
                </div>

                <p className="text-sm text-gray-400">
                  Сохраните или запомните этот код
                </p>
              </div>
            )}

            {isFailed && (
              <div className="text-center mb-6">
                <p className="text-xl font-bold text-red-400 mb-4">
                  Код неправильный!
                </p>
                <p className="text-white mb-4">
                  Ваша попытка: {inputDigits.join("")}
                </p>
                <div className="flex justify-center gap-4 mb-6">
                  {inputDigits.map((digit, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-600 text-white rounded">
                        {digit}
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full ${getResultColor(failedResults[index])} shadow-lg`}
                        style={{
                          boxShadow:
                            failedResults[index] === "correct"
                              ? "0 0 10px rgba(34, 197, 94, 0.8)"
                              : failedResults[index] === "present"
                                ? "0 0 10px rgba(234, 179, 8, 0.8)"
                                : "0 0 10px rgba(239, 68, 68, 0.8)",
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Легенда цветов */}
                <div className="mb-4 bg-gray-700 rounded-lg p-4 text-left">
                  <p className="text-white text-sm mb-2 font-semibold">
                    Значение цветов:
                  </p>
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span className="text-white">
                        Правильная цифра на своем месте
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                      <span className="text-white">
                        Цифра есть, но не на своем месте
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      <span className="text-white">Цифры нет в коде</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-400">
                  Код доступа сгорел. Получите новый код, чтобы попробовать
                  снова.
                </p>
              </div>
            )}

            {!isSuccess && !isFailed && (
              <>
                {/* Легенда цветов */}
                <div className="mb-6 bg-gray-700 rounded-lg p-4">
                  <p className="text-white text-sm mb-2 font-semibold">
                    Значение цветов:
                  </p>
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span className="text-white">
                        Правильная цифра на своем месте
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                      <span className="text-white">
                        Цифра есть, но не на своем месте
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      <span className="text-white">Цифры нет в коде</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4 p-3 bg-yellow-900 bg-opacity-50 rounded-lg border border-yellow-600">
                  <p className="text-yellow-200 text-sm text-center font-semibold">
                    ⚠️ У вас только одна попытка!
                  </p>
                </div>

                {/* Ввод цифр */}
                <div className="flex justify-center gap-4 mb-6">
                  {[0, 1, 2].map((index) => (
                    <input
                      key={index}
                      id={`digit-${index}`}
                      type="text"
                      maxLength={1}
                      value={inputDigits[index]}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      disabled={isSubmitting}
                      className="w-16 h-16 text-center text-3xl font-bold bg-gray-700 text-white border-4 border-yellow-600 rounded-lg focus:outline-none focus:ring-4 focus:ring-yellow-400 disabled:opacity-50"
                    />
                  ))}
                </div>

                {/* Кнопка проверки */}
                <button
                  onClick={checkCode}
                  disabled={inputDigits.some((d) => d === "") || isSubmitting}
                  className="w-full py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white font-bold text-lg rounded-lg hover:from-yellow-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? "Проверка..." : "Проверить код"}
                </button>
              </>
            )}

            {/* Кнопка закрытия */}
            {!isSubmitting && (isSuccess || isFailed) && (
              <button
                onClick={() => setIsCracking(false)}
                className="w-full mt-4 py-2 bg-gray-600 text-white font-bold rounded-lg hover:bg-gray-500 transition-all"
              >
                Закрыть
              </button>
            )}

            {!isSuccess && !isFailed && (
              <button
                onClick={() => setIsCracking(false)}
                disabled={isSubmitting}
                className="w-full mt-4 py-2 bg-gray-600 text-white font-bold rounded-lg hover:bg-gray-500 transition-all disabled:opacity-50"
              >
                Отменить
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
